import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Eye, EyeOff, LogIn } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/forms/Field';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { isApiError } from '@/utils/ApiError';
import { ROUTES } from '@/app/router/routes';
import { useAuth } from './useAuth';

const schema = z.object({
  email: z.string().min(1, 'E-mail address is required').email('Enter a valid e-mail address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof schema>;

interface RedirectState {
  from?: { pathname: string };
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await login(values.email, values.password);
      // Return the user to whatever they were trying to reach before the
      // redirect, rather than always dropping them on the dashboard.
      const state = location.state as RedirectState | null;
      navigate(state?.from?.pathname ?? ROUTES.dashboard, { replace: true });
    } catch (error) {
      /*
       * The server answers all credential failures identically, on purpose, so
       * this message stays deliberately vague. Guessing at a friendlier
       * "no account with that address" here would undo that.
       */
      setFormError(
        isApiError(error) && error.status === 401
          ? 'Incorrect e-mail address or password.'
          : 'Could not sign in. Please try again.',
      );
    }
  });

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="animate-rise relative w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center bg-accent text-xl font-extrabold text-accent-fg">
            S
          </div>
          <h1 className="text-xl font-semibold tracking-tight text-ink">School Management System</h1>
          <p className="mt-1 text-sm text-muted">Sign in to continue</p>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          className="space-y-4 rounded-2xl border border-line bg-surface p-6 shadow-lg"
        >
          {formError !== null && (
            <div
              role="alert"
              className="animate-fade-in flex items-start gap-2.5 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2.5 text-sm text-danger"
            >
              <AlertCircle className="mt-px h-4 w-4 shrink-0" aria-hidden="true" />
              {formError}
            </div>
          )}

          <TextField
            label="E-mail address"
            type="email"
            autoComplete="username"
            placeholder="you@school.local"
            autoFocus
            required
            error={errors.email?.message}
            {...register('email')}
          />

          <div className="relative">
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••••"
              required
              className="pr-10"
              error={errors.password?.message}
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((shown) => !shown)}
              className="absolute right-2 top-[1.85rem] p-1.5 text-subtle transition-colors hover:bg-surface-2 hover:text-ink"              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>

          <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
            {!isSubmitting && <LogIn className="h-4 w-4" aria-hidden="true" />}
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
